import { tabletSidebar } from "./index-tablet";
import { mobileSidebar } from "./index-mobile";
import { SIDEBAR_HANDLE_OVERLAP, SIDEBAR_WIDTH, STATE_CLOSE, STATE_OPEN, STORAGE_KEY } from "../shared/constant";
import { DesktopSidebarOptions, DeviceType, Orientation, SidebarController, SidebarElements, SidebarFallbackOptions, SidebarState } from "../types/index-type";


const selectSidebarElements = (): SidebarElements => {
  return {
    grid: document.querySelector<HTMLElement>(".grid"),
    sidebar: document.querySelector<HTMLElement>(".sidebar"),
    main: document.querySelector<HTMLElement>(".main"),
    handle: document.querySelector<HTMLElement>(".opcl-button"),
  };
};

type DesktopAnimatedContent = HTMLElement & {
  __desktopSidebarStartTimer?: number;
  __desktopSidebarCleanupTimer?: number;
};

const DESKTOP_TRANSITION_MS = 500;

type ResponsiveBand = "smartphone" | "tablet" | "pc";

const getViewportWidth = (): number => {
  return window.visualViewport?.width ?? window.innerWidth;
};

const getViewportHeight = (): number => {
  return window.visualViewport?.height ?? window.innerHeight;
};

const isShortMobileLandscape = (
  width: number = getViewportWidth(),
  height: number = getViewportHeight()
): boolean => {
  return width > height && height <= 500;
};

const getResponsiveBand = (
  width: number = window.innerWidth,
  height: number = window.innerHeight
): ResponsiveBand => {
  if ((width > height && height <= 500) || width < 768) {
    return "smartphone";
  }

  return width >= 1200 ? "pc" : "tablet";
};

const getSidebarHandleOverlap = (): string => {
  const isSmartphonePortrait =
    getViewportWidth() < 768 &&
    window.matchMedia("(orientation: portrait)").matches;

  return isSmartphonePortrait
    ? `calc(${SIDEBAR_HANDLE_OVERLAP} + 1px)`
    : SIDEBAR_HANDLE_OVERLAP;
};

type SidebarViewportBand = "narrow" | "wide";

const SIDEBAR_BREAKPOINT_PX = 768;
const SIDEBAR_TOTAL_WIDTH = "30rem";
const SIDEBAR_CONTENT_WIDTH = "26rem";
const SIDEBAR_HANDLE_WIDTH = "var(--sidebar-toggle-column-width)";
const SIDEBAR_CLOSED_SAFE_WIDTH = "calc(var(--sidebar-toggle-column-width) + 2rem)";
const SIDEBAR_HANDLE_HEIGHT = "20rem";
const SIDEBAR_HANDLE_HEIGHT_REM = 20;
const SIDEBAR_HANDLE_TOP_FALLBACK = "calc(50dvh - 10rem)";
const SIDEBAR_HANDLE_TOP = `var(--sidebar-handle-top, ${SIDEBAR_HANDLE_TOP_FALLBACK})`;
const SIDEBAR_TRANSITION =
  "grid-template-columns 0.4s ease, left 0.4s ease, width 0.4s ease, min-width 0.4s ease, max-width 0.4s ease";

const getSidebarViewportBand = (): SidebarViewportBand => {
  const width = getViewportWidth();
  const height = getViewportHeight();

  if (width < SIDEBAR_BREAKPOINT_PX || isShortMobileLandscape(width, height)) {
    return "narrow";
  }

  return "wide";
};

const getRootFontSizePx = (): number => {
  const rootFontSize = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(rootFontSize) && rootFontSize > 0 ? rootFontSize : 10;
};

const syncSidebarViewportMetrics = (): void => {
  const viewportHeight = getViewportHeight();
  const viewportOffsetTop = window.visualViewport?.offsetTop ?? 0;
  const handleHeight = SIDEBAR_HANDLE_HEIGHT_REM * getRootFontSizePx();
  const top = Math.max(0, viewportOffsetTop + (viewportHeight - handleHeight) / 2);

  document.documentElement.style.setProperty("--sidebar-handle-top", `${top}px`);
};

let sidebarViewportMetricsFrame: number | null = null;

const scheduleSidebarViewportMetricSync = (): void => {
  if (sidebarViewportMetricsFrame !== null) {
    window.cancelAnimationFrame(sidebarViewportMetricsFrame);
  }

  sidebarViewportMetricsFrame = window.requestAnimationFrame(() => {
    sidebarViewportMetricsFrame = null;
    syncSidebarViewportMetrics();
  });
};

const getDefaultSidebarState = (band: SidebarViewportBand): SidebarState =>
  band === "wide" ? STATE_OPEN : STATE_CLOSE;

const readSidebarState = (fallback: SidebarState): SidebarState => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === STATE_OPEN || stored === STATE_CLOSE ? stored : fallback;
};

const applyUnifiedSidebarState = (
  state: SidebarState,
  options: { animate?: boolean; persist?: boolean } = {}
): void => {
  const { animate = true, persist = true } = options;
  const { grid, sidebar, main, handle } = selectSidebarElements();

  if (!grid || !sidebar || !main || !handle) {
    return;
  }

  syncSidebarViewportMetrics();

  const band = getSidebarViewportBand();
  const isWide = band === "wide";
  const isOpen = state === STATE_OPEN;
  const transition = animate ? SIDEBAR_TRANSITION : "0s";
  const handleOverlap = SIDEBAR_HANDLE_OVERLAP;

  grid.style.transition = transition;
  sidebar.style.transition = transition;
  main.style.transition = transition;
  handle.style.transition = transition;

  grid.style.left = "0";
  if (isWide) {
    const sidebarColumnWidth = isOpen ? SIDEBAR_TOTAL_WIDTH : SIDEBAR_CLOSED_SAFE_WIDTH;
    grid.style.setProperty("grid-template-columns", `${sidebarColumnWidth} minmax(0, 1fr)`, "important");
  } else {
    grid.style.removeProperty("grid-template-columns");
  }

  sidebar.style.position = "fixed";
  sidebar.style.top = "0";
  sidebar.style.bottom = "0";
  sidebar.style.width = isWide ? SIDEBAR_TOTAL_WIDTH : SIDEBAR_CONTENT_WIDTH;
  sidebar.style.minWidth = isWide ? SIDEBAR_TOTAL_WIDTH : SIDEBAR_CONTENT_WIDTH;
  sidebar.style.maxWidth = isWide ? SIDEBAR_TOTAL_WIDTH : SIDEBAR_CONTENT_WIDTH;
  sidebar.style.gridTemplateColumns = isWide
    ? `${SIDEBAR_CONTENT_WIDTH} ${SIDEBAR_HANDLE_WIDTH}`
    : SIDEBAR_CONTENT_WIDTH;
  sidebar.style.left = isOpen ? "0" : `calc(-1 * ${SIDEBAR_CONTENT_WIDTH})`;

  main.classList.toggle("main--sidebar-overlay", !isWide && isOpen);
  main.style.gridColumn = isWide ? "2 / 3" : "";
  main.style.left = "0";
  main.style.width = "100%";

  sidebar.classList.toggle("sidebar--open", isOpen);
  sidebar.classList.toggle("sidebar--closed", !isOpen);

  handle.style.position = "fixed";
  handle.style.top = SIDEBAR_HANDLE_TOP;
  handle.style.left = isOpen
    ? `calc(${SIDEBAR_CONTENT_WIDTH} - ${handleOverlap})`
    : `calc(-1 * ${handleOverlap})`;
  handle.style.transform = "none";
  handle.style.margin = "0";
  handle.style.marginTop = "0";
  handle.style.width = SIDEBAR_HANDLE_WIDTH;
  handle.style.minWidth = SIDEBAR_HANDLE_WIDTH;
  handle.style.maxWidth = SIDEBAR_HANDLE_WIDTH;
  handle.style.height = SIDEBAR_HANDLE_HEIGHT;
  handle.style.zIndex = "2000";
  handle.textContent = isOpen ? "CLOSE" : "OPEN";

  if (persist) {
    localStorage.setItem(STORAGE_KEY, state);
  }
};

const initUnifiedSidebar = (): void => {
  const { handle } = selectSidebarElements();

  if (!handle || handle.dataset.unifiedSidebarBound === "true") {
    return;
  }

  let currentBand = getSidebarViewportBand();
  applyUnifiedSidebarState(getDefaultSidebarState(currentBand), {
    animate: false,
  });

  handle.addEventListener("click", () => {
    const fallback = getDefaultSidebarState(getSidebarViewportBand());
    const nextState = readSidebarState(fallback) === STATE_OPEN ? STATE_CLOSE : STATE_OPEN;
    applyUnifiedSidebarState(nextState);
  });
  handle.dataset.unifiedSidebarBound = "true";

  let resizeFrame: number | null = null;
  const syncForViewport = (): void => {
    if (resizeFrame !== null) {
      window.cancelAnimationFrame(resizeFrame);
    }

    resizeFrame = window.requestAnimationFrame(() => {
      resizeFrame = null;
      syncSidebarViewportMetrics();
      const nextBand = getSidebarViewportBand();
      const crossedBreakpoint = nextBand !== currentBand;
      currentBand = nextBand;

      const nextState = crossedBreakpoint
        ? getDefaultSidebarState(nextBand)
        : readSidebarState(getDefaultSidebarState(nextBand));

      applyUnifiedSidebarState(nextState, {
        animate: crossedBreakpoint,
      });
    });
  };

  window.addEventListener("resize", syncForViewport);
  window.addEventListener("orientationchange", syncForViewport);
  window.visualViewport?.addEventListener("resize", syncForViewport);
  window.visualViewport?.addEventListener("scroll", scheduleSidebarViewportMetricSync);
};

const initSidebarTreeMenu = (): void => {
  document.querySelectorAll<HTMLButtonElement>("[data-sidebar-tree-toggle]").forEach((toggle) => {
    if (toggle.dataset.sidebarTreeBound === "true") {
      return;
    }

    const controls = toggle.getAttribute("aria-controls");
    const children = controls ? document.getElementById(controls) : null;

    if (!children) {
      return;
    }

    toggle.setAttribute("aria-expanded", "false");
    children.hidden = true;

    toggle.addEventListener("click", () => {
      const isExpanded = toggle.getAttribute("aria-expanded") === "true";
      toggle.setAttribute("aria-expanded", String(!isExpanded));
      children.hidden = isExpanded;
    });

    toggle.dataset.sidebarTreeBound = "true";
  });
};

const getDesktopContentWrapper = (
  main: HTMLElement
): DesktopAnimatedContent | null => {
  const firstChild = main.firstElementChild;
  return firstChild instanceof HTMLElement ? (firstChild as DesktopAnimatedContent) : null;
};

const clearDesktopContentAnimation = (
  contentWrapper: DesktopAnimatedContent
): void => {
  if (contentWrapper.__desktopSidebarStartTimer) {
    window.clearTimeout(contentWrapper.__desktopSidebarStartTimer);
    delete contentWrapper.__desktopSidebarStartTimer;
  }

  if (contentWrapper.__desktopSidebarCleanupTimer) {
    window.clearTimeout(contentWrapper.__desktopSidebarCleanupTimer);
    delete contentWrapper.__desktopSidebarCleanupTimer;
  }

  contentWrapper.style.transition = "";
  contentWrapper.style.position = "";
  contentWrapper.style.left = "";
  contentWrapper.style.width = "";
  contentWrapper.style.pointerEvents = "";
};

const animateDesktopContentWrapper = (
  main: HTMLElement,
  animate: boolean,
  applyLayoutChange: () => void
): void => {
  const contentWrapper = getDesktopContentWrapper(main);
  const shouldAnimate =
    animate &&
    contentWrapper !== null &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (!contentWrapper || !shouldAnimate) {
    if (contentWrapper) {
      clearDesktopContentAnimation(contentWrapper);
    }
    applyLayoutChange();
    return;
  }

  clearDesktopContentAnimation(contentWrapper);
  const fromRect = contentWrapper.getBoundingClientRect();
  applyLayoutChange();
  const toRect = contentWrapper.getBoundingClientRect();

  const offsetLeft = fromRect.left - toRect.left;
  const widthDelta = Math.abs(fromRect.width - toRect.width);

  if (Math.abs(offsetLeft) < 0.5 && widthDelta < 0.5) {
    return;
  }

  contentWrapper.style.transition = "none";
  contentWrapper.style.position = "relative";
  contentWrapper.style.left = `${offsetLeft}px`;
  contentWrapper.style.width = `${fromRect.width}px`;
  contentWrapper.style.pointerEvents = "none";
  contentWrapper.getBoundingClientRect();

  contentWrapper.__desktopSidebarStartTimer = window.setTimeout(() => {
    contentWrapper.style.transition = `left ${DESKTOP_TRANSITION_MS}ms ease, width ${DESKTOP_TRANSITION_MS}ms ease`;
    contentWrapper.style.left = "0px";
    contentWrapper.style.width = `${toRect.width}px`;
    delete contentWrapper.__desktopSidebarStartTimer;
  }, 0);

  contentWrapper.__desktopSidebarCleanupTimer = window.setTimeout(() => {
    clearDesktopContentAnimation(contentWrapper);
  }, DESKTOP_TRANSITION_MS + 50);
};

// Ensure a persisted sidebar state exists and return it.
const ensureSidebarState = (
  defaultState: SidebarState = STATE_OPEN,
  storageKey: string = STORAGE_KEY
): SidebarState => {
  const stored = localStorage.getItem(storageKey);
  if (stored === STATE_OPEN || stored === STATE_CLOSE) {
    return stored;
  }

  localStorage.setItem(storageKey, defaultState);
  return defaultState;
};

// Apply positional styles for the sidebar toggle based on the requested state.
const applyDesktopSidebarState = (
  state: SidebarState,
  options: DesktopSidebarOptions = {}
): void => {
  const { overlay = false, storageKey = STORAGE_KEY, animate = true } = options;
  const { grid, sidebar, main, handle } = selectSidebarElements();

  if (!grid || !sidebar || !main || !handle) {
    return;
  }

  const transitionDuration = animate ? "0.5s" : "0s";
  grid.style.transition = transitionDuration;
  sidebar.style.transition = transitionDuration;
  main.style.transition = transitionDuration;
  handle.style.transition = transitionDuration;

  const isOpen = state === STATE_OPEN;
  const handleOverlap = getSidebarHandleOverlap();

  const applyDesktopLayout = (): void => {
    grid.style.left = "0";
    grid.style.gridTemplateColumns = "30rem minmax(0, 1fr)";
    sidebar.style.left = isOpen ? "0" : "-26rem";
    main.style.gridColumn = isOpen ? "2 / 3" : "1 / -1";
    main.style.left = "0";
    main.style.width = "100%";
    handle.style.position = "fixed";
    handle.style.top = "50%";
    handle.style.transform = "none";
    handle.style.marginTop = "calc(-0.5 * var(--sidebar-handle-height))";
    handle.style.height = "var(--sidebar-handle-height)";
    handle.style.zIndex = "2000";
    handle.style.left = isOpen ? `calc(${SIDEBAR_WIDTH} - ${handleOverlap})` : `calc(-1 * ${handleOverlap})`;
  };

  if (overlay) {
    grid.style.left = "0";
    sidebar.style.left = isOpen ? "0" : "-26rem";
    grid.style.gridTemplateColumns = "30rem minmax(0, 1fr)";
    main.style.gridColumn = "2 / 3";
    main.style.left = "0";
    main.style.width = "100%";
    handle.style.position = "fixed";
    handle.style.top = "50%";
    handle.style.transform = "none";
    handle.style.marginTop = "calc(-0.5 * var(--sidebar-handle-height))";
    handle.style.height = "var(--sidebar-handle-height)";
    handle.style.zIndex = "2000";
    handle.style.left = isOpen ? `calc(${SIDEBAR_WIDTH} - ${handleOverlap})` : `calc(-1 * ${handleOverlap})`;
  } else {
    animateDesktopContentWrapper(main, animate, applyDesktopLayout);
  }

  handle.textContent = isOpen ? "CLOSE" : "OPEN";

  localStorage.setItem(storageKey, isOpen ? STATE_OPEN : STATE_CLOSE);

  if (!animate) {
    requestAnimationFrame(() => {
      const animatedDuration = "0.5s";
      grid.style.transition = animatedDuration;
      sidebar.style.transition = animatedDuration;
      main.style.transition = animatedDuration;
      handle.style.transition = animatedDuration;
    });
  }
};

// Wire up desktop-style sidebar controls; optional overlay mode keeps main content fixed.
const setupDesktopSidebarFallback = (
  options: SidebarFallbackOptions = {}
): SidebarController | null => {
  const {
    lockOnNarrow = true,
    defaultState = STATE_OPEN,
    overlay = false,
    storageKey = STORAGE_KEY
  } = options;
  const handle = document.querySelector<HTMLElement>(".opcl-button");

  if (!handle || handle.dataset.sidebarFallbackBound === "true") {
    return null;
  }

  const applyStoredState = (animate = true): void => {
    const targetState = ensureSidebarState(defaultState, storageKey);
    applyDesktopSidebarState(targetState, { overlay, storageKey, animate });
  };

  applyStoredState(false);

  handle.addEventListener("click", () => {
    if (window.innerWidth < 1200 && handle.dataset.tabletSidebarBound === "true") {
      return;
    }

    const nextState =
      localStorage.getItem(storageKey) === STATE_OPEN ? STATE_CLOSE : STATE_OPEN;
    applyDesktopSidebarState(nextState, { overlay, storageKey });
  });

  handle.dataset.sidebarFallbackBound = "true";

  let hasInitialisedViewportSync = false;
  let lastResponsiveBand = getResponsiveBand();

  const syncForViewport = (): void => {
    const width = window.innerWidth;
    const nextResponsiveBand = getResponsiveBand(width, window.innerHeight);
    const crossedResponsiveBand = nextResponsiveBand !== lastResponsiveBand;
    const shouldAnimate = hasInitialisedViewportSync && !crossedResponsiveBand;

    if (nextResponsiveBand === "tablet") {
      tabletSidebar();

      if (handle.dataset.tabletSidebarBound === "true") {
        hasInitialisedViewportSync = true;
        lastResponsiveBand = nextResponsiveBand;
        return;
      }
    }

    if (width < 768) {
      if (lockOnNarrow) {
        applyDesktopSidebarState(STATE_CLOSE, {
          overlay,
          storageKey,
          animate: shouldAnimate
        });
      } else {
        applyStoredState(shouldAnimate);
      }
    } else {
      if (lockOnNarrow) {
        applyDesktopSidebarState(STATE_OPEN, {
          overlay,
          storageKey,
          animate: shouldAnimate
        });
        localStorage.setItem(storageKey, STATE_OPEN);
      } else {
        applyStoredState(shouldAnimate);
      }
    }

    hasInitialisedViewportSync = true;
    lastResponsiveBand = nextResponsiveBand;
  };

  window.addEventListener("load", syncForViewport);
  window.addEventListener("resize", syncForViewport);
  syncForViewport();

  return {
    setState: (state: SidebarState) => applyDesktopSidebarState(state, { overlay, storageKey })
  };
};

// Derive the current orientation for devices that expose window.orientation.
function getDeviceOrientation(
  deviceType: DeviceType
): { deviceType: DeviceType; orientation: Orientation } {
  const orientation: Orientation = (() => {
    if (typeof window.orientation === "number") {
      return Math.abs(window.orientation) === 90 ? "landscape" : "portrait";
    }

    if (window.matchMedia) {
      return window.matchMedia("(orientation: landscape)").matches
        ? "landscape"
        : "portrait";
    }

    return "portrait";
  })();
  // このタイミングでorientationAngleをsetしないと各デバイスごとの取得特にmobileで強制的にlandscapeになる
  localStorage.setItem("orientationAngle", orientation);
  return { deviceType, orientation };
}

// Infer device class from the supplied user agent string.
function detectDeviceType(userAgent: string): DeviceType {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const isShortLandscape = viewportWidth > viewportHeight && viewportHeight <= 500;

  if (isShortLandscape) {
    return "smartphone";
  }

  if (viewportWidth >= 1200) {
    return "pc";
  }

  if (viewportWidth >= 768) {
    return "tablet";
  }

  if (viewportWidth > 0) {
    return "smartphone";
  }

  const isMobile =
    /iPhone|iPod|Android(?=.*Mobile)|Windows Phone|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent
    );
  const isTablet =
    /iPad|Android(?!.*Mobile)|Windows(?=.*Touch)|Kindle|Silk|Tablet/i.test(userAgent);

  if (isTablet) {
    return "tablet";
  }

  if (isMobile) {
    return "smartphone";
  }

  return "pc";
}

// Capture orientation metadata once during startup for downstream helpers.
(() => {
  try {
    const deviceType = detectDeviceType(navigator.userAgent);
    getDeviceOrientation(deviceType);
  } catch (error) {
    if (error instanceof Error) {
      console.error(error.message);
    } else {
      console.error(error);
    }
  }
})();

// Report whether the viewport is currently portrait.
export const isPortrait = (): boolean => {
  return window.matchMedia("(orientation: portrait)").matches;
};

// Kick off unified sidebar logic once the DOM is ready.
document.addEventListener("DOMContentLoaded", () => {
  if (document.body) {
    document.body.classList.add("loaded");
  }

  const deviceType = detectDeviceType(navigator.userAgent);
  initUnifiedSidebar();
  initSidebarTreeMenu();

  const scrollLinks = document.querySelectorAll<HTMLAnchorElement>(".scroll-link");

  scrollLinks.forEach((link) => {
    link.addEventListener("click", (event) => {
      event.preventDefault();
      const targetId = link.getAttribute("data-target");
      if (!targetId) {
        return;
      }

      const targetElement = document.getElementById(targetId);

      if (!targetElement) {
        return;
      }

      if (deviceType === "smartphone" && isPortrait()) {
        window.setTimeout(() => {
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest"
          });
        }, 1000);
      } else {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest"
        });
      }
    });
  });

  const hljs = (window as Window & { hljs?: { highlightAll?: () => void } }).hljs;
  if (hljs?.highlightAll) {
    hljs.highlightAll();
  }

  const SwiperCtor = (window as Window & { Swiper?: new (...args: any[]) => unknown }).Swiper;
  if (SwiperCtor && document.querySelector(".swiper")) {
    new SwiperCtor(".swiper", {
      direction: "horizontal",
      loop: false,
      scrollbar: {
        el: ".swiper-scrollbar",
      },
      slidesPerView: "auto",
      spaceBetween: 15,
      pagination: {
        el: ".swiper-pagination",
        clickable: true,
      },
    });
  }

  const jq = (window as Window & { jQuery?: any; $?: any }).jQuery || (window as Window & { $?: any }).$;
  if (jq) {
    jq(() => {
      const $topButtons = jq(".topBtn");
      if (!$topButtons.length) {
        return;
      }

      const scrollThreshold = 500;
      const $window = jq(window);
      const $main = jq(".main");

      const getScrollPosition = () => {
        const windowScroll = $window.scrollTop();
        const mainScroll = $main.length ? $main.scrollTop() : 0;
        return Math.max(windowScroll || 0, mainScroll || 0);
      };

      const toggleTopButton = () => {
        if (getScrollPosition() > scrollThreshold) {
          $topButtons.stop(true, true).fadeIn();
        } else {
          $topButtons.stop(true, true).fadeOut();
        }
      };

      const scrollToTop = () => {
        if ($main.length && $main[0].scrollHeight > $main.innerHeight()) {
          $main.stop().animate({ scrollTop: 0 }, "slow");
        }

        jq("html, body").stop().animate({ scrollTop: 0 }, "slow");
      };

      $window.on("scroll", toggleTopButton);
      if ($main.length) {
        $main.on("scroll", toggleTopButton);
      }

      $topButtons.on("click", (event: Event) => {
        event.preventDefault();
        scrollToTop();
      });

      toggleTopButton();
    });
  }

  const codeBlocks = document.querySelectorAll("pre");
  if (codeBlocks.length) {
    codeBlocks.forEach((block) => {
      const button = document.createElement("button");
      button.className = "copy-code-btn";
      button.textContent = "Copy code";

      button.addEventListener("click", () => {
        const code = block.querySelector("code");
        if (!code) {
          return;
        }
        const range = document.createRange();
        range.selectNode(code);

        const selection = window.getSelection();
        if (!selection) {
          return;
        }
        selection.removeAllRanges();
        selection.addRange(range);

        document.execCommand("copy");
        selection.removeAllRanges();

        button.textContent = "Copied!";
        window.setTimeout(() => {
          button.textContent = "Copy code";
        }, 2000);
      });

      block.appendChild(button);
    });
  }
});
